import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, XIcon } from 'lucide-react';
const voiceEvolution = {
  retired: ['Playful tone with excessive exclamations', '"WOW factor" and magic language', '"Rock the visual game" — tryhard energy', 'Worn-out "journey/trip" metaphor', 'Dual-language headlines (2023 style)', 'Hand-drawn annotations & cute stickers'],
  evolved: ['Direct — still "you" and "we", but adult conversation, not forced enthusiasm', 'Transit-coded — the metro metaphor deepens: connection, network, infrastructure', 'End-to-end integration — still the core differentiator', 'Calm confidence — energy comes from solid ideas, not punctuation'],
  additions: [{
    title: 'Systems-thinking',
    body: 'We talk like people who understand how campaigns work end-to-end, how the pieces connect, how strategy becomes execution.'
  }, {
    title: 'Tech-literate, human-centered',
    body: 'Comfortable with AI, automation, new tools — but always in service of human stories and real business outcomes.'
  }, {
    title: 'Post-hype realism',
    body: 'The tone of people who have watched trends come and go, who build for durability, not virality.'
  }, {
    title: 'Barcelona, but global',
    body: 'Local roots, international fluency. Not trying to be Silicon Valley or Madison Avenue.'
  }]
};
const beforeAfter = [{
  old: "Let's create some magic together!",
  neu: "Let's create something that matters."
}, {
  old: 'Ready to rock the visual game?!',
  neu: 'Ready to build something real?'
}, {
  old: "Don't wait to add some WOW to your brand!",
  neu: 'Your brand deserves strategy that works.'
}, {
  old: 'What a trip!',
  neu: "Let's see where this goes."
}];
const newCTAs = [{
  cta: 'Map your next campaign',
  use: 'Strategy / consulting pages. Suggests planning, not selling.'
}, {
  cta: "Let's connect the dots",
  use: 'General inquiry. Honest about the work (synthesis, connection) without selling magic.'
}, {
  cta: 'Start the conversation',
  use: 'Softer entry point. No transit metaphor — just a human invitation.'
}, {
  cta: 'See where this goes',
  use: 'Portfolio / case study CTA. Implies journey without forcing the ticket metaphor.'
}, {
  cta: 'Build something that moves people',
  use: 'Hero CTA on the homepage. Double meaning — emotional movement and literal transit. Ambitious, not hyperbolic.'
}];
const vocabularyUse = [{
  tier: 'Tier 1 — Core',
  note: 'Use freely. These words define how Metro sounds.',
  words: ['connect', 'connection', 'network', 'line', 'route', 'station', 'infrastructure', 'build', 'craft', 'make', 'integrate', 'design', 'execute', 'campaign', 'strategy', 'story', 'content', 'production', 'real', 'honest', 'clear', 'direct', 'intentional', 'people', 'audience', 'client', 'collaborator']
}, {
  tier: 'Tier 2 — Descriptive',
  note: 'Use when describing how the work feels.',
  words: ['seamless', 'end-to-end', 'integrated', 'cohesive', 'thoughtful', 'considered', 'deliberate', 'human', 'personal', 'authentic', 'efficient', 'systematic', 'reliable']
}, {
  tier: 'Tier 3 — Aspirational',
  note: 'Use sparingly. Reserve for moments that earn the weight.',
  words: ['impact', 'movement', 'momentum', 'transform', 'shift', 'elevate', 'bold', 'ambitious', 'committed']
}];
const vocabularyAvoid = [{
  word: 'Magic',
  why: 'Implies tricks instead of craft. Undermines the systematic, reliable promise.'
}, {
  word: 'WOW factor',
  why: '2015 client-speak. Vague. Says nothing about what makes the work effective.'
}, {
  word: 'Rock (the X)',
  why: "Tryhard energy. Not Metro's register."
}, {
  word: 'Unleash',
  why: 'Suggests chaos. Metro is about control, precision, intention.'
}, {
  word: 'Game-changer',
  why: "Overused. If you have to say it, it probably isn't."
}, {
  word: 'Cutting-edge',
  why: 'Ages badly. Implies chasing trends over timeless craft.'
}, {
  word: 'Synergy',
  why: 'Corporate jargon that glazes client eyes.'
}, {
  word: 'Guru / Ninja / Rockstar',
  why: '2012 startup clichés. Metro is serious professionals, not LinkedIn caricatures.'
}, {
  word: 'Disrupt',
  why: 'Tech-bro language. Metro builds, not destroys.'
}, {
  word: 'Journey',
  why: 'Overused metaphor that lost its meaning. Metro already has a better transit metaphor: connection.'
}];
export function MessagingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="messaging" ref={ref} className="min-h-screen px-8 py-24" data-id="element-1450">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-1451">
        <div className="mb-16" data-id="element-1452">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-1453">
            06 / VOICE
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-1454">
            Brand Voice · v2026
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 max-w-2xl font-helvetica" data-id="element-1455">
            Verbal positioning for 2026. The metro metaphor evolves from
            ticket-booth playfulness into infrastructure for ideas. This
            document defines the tagline, manifesto, voice qualities,
            punctuation, CTAs, and vocabulary.
          </p>
        </div>

        <div className="space-y-20" data-id="element-1456">
          {/* 06.1 — Tagline */}
          <div data-id="element-1457">
            <div className="flex items-baseline justify-between mb-6" data-id="element-1458">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-1459">
                TAGLINE
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-1460">
                06.1
              </div>
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-12 md:p-16" data-id="element-1461">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center" data-id="element-1462">
                <div className="md:col-span-7" data-id="element-1463">
                  <div className="font-space font-bold text-5xl md:text-7xl leading-[0.9] tracking-tight text-[var(--accent-blue)]" data-id="element-1464">
                    Where ideas
                    <br data-id="element-1465" />
                    connect.
                  </div>
                </div>
                <div className="md:col-span-5" data-id="element-1466">
                  <div className="font-mono text-xs text-[var(--text-muted)] mb-3 tracking-wider" data-id="element-1467">
                    RATIONALE
                  </div>
                  <div className="font-helvetica text-base leading-relaxed text-[var(--text-ink)]" data-id="element-1468">
                    The metro metaphor evolves. It is not a ticket to "WOW" — it
                    is the infrastructure that connects ideas with audiences,
                    strategy with execution, brands with real impact.
                  </div>
                  <div className="mt-6 pt-4 border-t border-[var(--divider)]" data-id="element-1469">
                    <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider mb-1" data-id="element-1470">
                      RETIRES
                    </div>
                    <div className="font-helvetica text-sm text-[var(--text-muted)] line-through" data-id="element-1471">
                      The hands-on agency.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 06.2 — Manifesto */}
          <div data-id="element-1472">
            <div className="flex items-baseline justify-between mb-6" data-id="element-1473">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-1474">
                MANIFESTO · 80 WORDS
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-1475">
                06.2
              </div>
            </div>
            <div className="bg-[var(--accent-blue)] rounded-sm p-12 md:p-16 text-white" data-id="element-1476">
              <div className="font-space font-bold text-2xl md:text-3xl leading-[1.25] mb-8" data-id="element-1477">
                We don't believe in magic. We believe in systems that work.
              </div>
              <div className="font-helvetica text-base md:text-lg leading-relaxed max-w-2xl space-y-4 opacity-95" data-id="element-1478">
                <p data-id="element-1479">
                  In a world saturated with content, we build infrastructure for
                  ideas that last. Campaigns that connect strategy to execution.
                  Brands that move people. Stories that earn the attention they
                  get.
                </p>
                <p data-id="element-1480">
                  We don't chase trends. We build lines between what you need to
                  communicate and who needs to hear it. Technology is our tool,
                  but people drive the train.
                </p>
                <p data-id="element-1481">
                  Every campaign is a network. Every brand, a station where
                  something real happens.
                </p>
              </div>
              <div className="mt-10 pt-6 border-t border-white/20 font-space font-bold text-xl md:text-2xl" data-id="element-1482">
                Your ideas. Our lines. Let's move.
              </div>
            </div>
          </div>

          {/* 06.3 — Voice Evolution */}
          <div data-id="element-1483">
            <div className="flex items-baseline justify-between mb-6" data-id="element-1484">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-1485">
                VOICE EVOLUTION
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-1486">
                06.3
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1487">
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8" data-id="element-1488">
                <div className="font-mono text-[10px] text-[var(--text-muted)] mb-2 tracking-wider" data-id="element-1489">
                  RETIRED
                </div>
                <div className="font-space text-lg font-medium mb-6 text-[var(--text-muted)]" data-id="element-1490">
                  What we leave behind
                </div>
                <ul className="space-y-3" data-id="element-1491">
                  {voiceEvolution.retired.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-1492">
                      <div className="w-4 h-4 rounded-full bg-[var(--bg-canvas)] flex-shrink-0 flex items-center justify-center mt-0.5" data-id="element-1493">
                        <XIcon className="w-2.5 h-2.5 text-[var(--text-muted)]" strokeWidth={2.5} data-id="element-1494" />
                      </div>
                      <span className="font-helvetica text-sm text-[var(--text-muted)] line-through leading-snug" data-id="element-1495">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8" data-id="element-1496">
                <div className="font-mono text-[10px] text-[var(--text-muted)] mb-2 tracking-wider" data-id="element-1497">
                  EVOLVED
                </div>
                <div className="font-space text-lg font-medium mb-6" data-id="element-1498">
                  What we carry forward
                </div>
                <ul className="space-y-3" data-id="element-1499">
                  {voiceEvolution.evolved.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-1500">
                      <div className="w-4 h-4 rounded-full bg-[var(--accent-blue)]/10 flex-shrink-0 flex items-center justify-center mt-0.5" data-id="element-1501">
                        <CheckIcon className="w-2.5 h-2.5 text-[var(--accent-blue)]" strokeWidth={2.5} data-id="element-1502" />
                      </div>
                      <span className="font-helvetica text-sm leading-snug" data-id="element-1503">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="bg-[var(--accent-blue)] rounded-sm p-8 text-white" data-id="element-1504">
                <div className="font-mono text-[10px] opacity-70 mb-2 tracking-wider" data-id="element-1505">
                  2026 ADDITIONS
                </div>
                <div className="font-space text-lg font-medium mb-6" data-id="element-1506">
                  What is new
                </div>
                <ul className="space-y-5" data-id="element-1507">
                  {voiceEvolution.additions.map(item => <li key={item.title} data-id="element-1508">
                      <div className="font-space text-sm font-medium mb-1" data-id="element-1509">
                        {item.title}
                      </div>
                      <div className="font-helvetica text-xs opacity-90 leading-relaxed" data-id="element-1510">
                        {item.body}
                      </div>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* 06.4 — Punctuation rule */}
          <div data-id="element-1511">
            <div className="flex items-baseline justify-between mb-6" data-id="element-1512">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-1513">
                PUNCTUATION RULE
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-1514">
                06.4
              </div>
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10" data-id="element-1515">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10" data-id="element-1516">
                <div className="md:col-span-5" data-id="element-1517">
                  <div className="font-space font-medium text-3xl leading-tight mb-4" data-id="element-1518">
                    Periods are the
                    <br data-id="element-1519" />
                    new exclamation points.
                  </div>
                  <div className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed" data-id="element-1520">
                    If the line is good, it doesn't need extra energy. If it
                    isn't, an exclamation won't save it.
                  </div>
                </div>
                <div className="md:col-span-7" data-id="element-1521">
                  <div className="font-mono text-[10px] text-[var(--text-muted)] mb-3 tracking-wider" data-id="element-1522">
                    EXCEPTIONS
                  </div>
                  <ul className="space-y-2.5" data-id="element-1523">
                    {['Client testimonials — their voice, their punctuation', 'Genuine team milestones (a launch, an award)', 'Deliberately playful social where the exclamation IS the joke'].map(e => <li key={e} className="flex items-start gap-2.5" data-id="element-1524">
                        <div className="w-1 h-1 rounded-full bg-[var(--accent-blue)] mt-2 flex-shrink-0" data-id="element-1525" />
                        <span className="font-helvetica text-sm leading-relaxed" data-id="element-1526">
                          {e}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="pt-8 border-t border-[var(--divider)]" data-id="element-1527">
                <div className="font-mono text-[10px] text-[var(--text-muted)] mb-4 tracking-wider" data-id="element-1528">
                  BEFORE / AFTER
                </div>
                <div className="divide-y divide-[var(--divider)]" data-id="element-1529">
                  {beforeAfter.map(pair => <div key={pair.old} className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4" data-id="element-1530">
                      <div className="font-helvetica text-sm text-[var(--text-muted)] line-through" data-id="element-1531">
                        "{pair.old}"
                      </div>
                      <div className="font-space text-sm font-medium" data-id="element-1532">
                        "{pair.neu}"
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* 06.5 — CTAs */}
          <div data-id="element-1533">
            <div className="flex items-baseline justify-between mb-6" data-id="element-1534">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-1535">
                CTAs · v2026
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-1536">
                06.5
              </div>
            </div>
            <div className="mb-4 max-w-2xl" data-id="element-1537">
              <p className="font-helvetica text-sm text-[var(--text-muted)] leading-relaxed" data-id="element-1538">
                Moving away from "Board the agency" and "Let's chat" toward CTAs
                that sound like genuine collaboration, not ticket-booth
                transactions.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1539">
              {newCTAs.map((c, i) => <motion.div key={c.cta} initial={{
              opacity: 0,
              y: 10
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.3,
              delay: i * 0.05
            }} className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-6 flex items-start gap-5" data-id="element-1540">
                  <div className="font-mono text-[10px] text-[var(--accent-blue)] mt-1 flex-shrink-0" data-id="element-1541">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1542">
                    <div className="font-space font-medium text-xl mb-2 leading-tight" data-id="element-1543">
                      {c.cta}
                    </div>
                    <div className="font-helvetica text-xs text-[var(--text-muted)] leading-relaxed" data-id="element-1544">
                      {c.use}
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* 06.6 — Vocabulary */}
          <div data-id="element-1545">
            <div className="flex items-baseline justify-between mb-6" data-id="element-1546">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-1547">
                VOCABULARY
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-1548">
                06.6
              </div>
            </div>

            <div className="space-y-4 mb-10" data-id="element-1549">
              {vocabularyUse.map(tier => <div key={tier.tier} className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8" data-id="element-1550">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-id="element-1551">
                    <div className="md:col-span-3" data-id="element-1552">
                      <div className="font-mono text-[10px] text-[var(--accent-blue)] mb-2 tracking-wider" data-id="element-1553">
                        USE
                      </div>
                      <div className="font-space font-medium" data-id="element-1554">{tier.tier}</div>
                      <div className="font-helvetica text-xs text-[var(--text-muted)] mt-2 leading-relaxed" data-id="element-1555">
                        {tier.note}
                      </div>
                    </div>
                    <div className="md:col-span-9 flex flex-wrap gap-2" data-id="element-1556">
                      {tier.words.map(w => <span key={w} className="px-3 py-1 rounded-full bg-[var(--bg-canvas)] font-helvetica text-sm" data-id="element-1557">
                          {w}
                        </span>)}
                    </div>
                  </div>
                </div>)}
            </div>

            <div className="bg-[var(--text-ink)] rounded-sm p-10 text-white" data-id="element-1558">
              <div className="font-mono text-[10px] text-white/60 mb-2 tracking-wider" data-id="element-1559">
                NEVER USE
              </div>
              <div className="font-space font-medium text-xl mb-6" data-id="element-1560">
                Words Metro retires
              </div>
              <div className="divide-y divide-white/10" data-id="element-1561">
                {vocabularyAvoid.map(v => <div key={v.word} className="py-3 grid grid-cols-1 md:grid-cols-12 gap-4" data-id="element-1562">
                    <div className="md:col-span-3 font-space font-medium line-through opacity-80" data-id="element-1563">
                      {v.word}
                    </div>
                    <div className="md:col-span-9 font-helvetica text-sm opacity-70 leading-relaxed" data-id="element-1564">
                      {v.why}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}